/**
 * Admin Service
 *
 * Handles admin-only API calls for tier management, promo codes, analytics, and user management.
 */

import { apiRequest } from '../utils/apiClient';
import { getAuthToken } from '../utils/authToken';
import type { CoinTransaction } from './coinService';

// Re-export so admin pages can import the coin transaction type from here too.
export type { CoinTransaction, CoinTxnType } from './coinService';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Polymorphic discount shape shared by add-on bundles and custom plans.
 *  - NONE:       no discount
 *  - PERCENTAGE: discountValue% off the base
 *  - FIXED_SAR:  discountValue SAR off
 *  - COINS:      discountValue coins → SAR off at the live coin rate
 */
export type DiscountKind = 'NONE' | 'PERCENTAGE' | 'FIXED_SAR' | 'COINS';

export interface SubscriptionTier {
  id: string;
  name: string;
  nameAr?: string;
  slug: string;
  description?: string;
  descriptionAr?: string;
  priceMonthly: number;
  priceYearly: number;
  currency: string;
  features?: Record<string, unknown>;
  isPopular: boolean;
  isActive: boolean;
  sortOrder: number;
  planType: string;
  maxListings: number;
  maxSubUsers: number;
  /** Coins granted to the user when this plan is purchased/renewed. */
  includedCoins?: number;
  supportTier?: string;
  /** Custom (bespoke) plan flag. */
  isCustom?: boolean;
  /** Custom-plan discount kind. */
  discountKind?: DiscountKind;
  /** Custom-plan discount value (interpreted per {@link DiscountKind}). */
  discountValue?: number;
  /** Embedded add-ons granted with this plan (returned with each item's `addon`). */
  addons?: PlanAddon[];
  createdAt: string;
  updatedAt: string;
}

/** An add-on embedded in a subscription plan, with its quantity + add-on detail. */
export interface PlanAddon {
  id?: string;
  planId?: string;
  addonId: string;
  quantity: number;
  addon?: Pick<Addon, 'id' | 'slug' | 'name' | 'nameAr' | 'family' | 'fulfillment' | 'priceSar'>;
}

export interface CreateTierParams {
  name: string;
  nameAr?: string;
  slug: string;
  description?: string;
  descriptionAr?: string;
  priceMonthly: number;
  priceYearly: number;
  features?: Record<string, unknown>;
  isPopular?: boolean;
  sortOrder?: number;
  planType: 'broker' | 'company';
  maxListings?: number;
  maxSubUsers?: number;
  includedCoins?: number;
  /** Custom (bespoke) plan flag. */
  isCustom?: boolean;
  /** Custom-plan discount kind. */
  discountKind?: DiscountKind;
  /** Custom-plan discount value (interpreted per {@link DiscountKind}). */
  discountValue?: number;
  /** Embedded add-ons to grant with this plan (the relation is replaced on update). */
  addons?: { addonId: string; quantity: number }[];
}

export interface PromoCode {
  id: string;
  code: string;
  description?: string;
  descriptionAr?: string;
  type: 'PERCENTAGE' | 'FIXED_AMOUNT' | 'FREE_TRIAL' | 'FEATURE_UNLOCK';
  value: number;
  planTypes?: string[];
  planIds?: string[];
  maxUses?: number;
  maxUsesPerUser: number;
  validFrom: string;
  validUntil?: string;
  isActive: boolean;
  currentUses: number;
  createdAt: string;
  createdBy?: string;
}

export interface CreatePromoCodeParams {
  code: string;
  description?: string;
  descriptionAr?: string;
  type: 'PERCENTAGE' | 'FIXED_AMOUNT' | 'FREE_TRIAL' | 'FEATURE_UNLOCK';
  value: number;
  planTypes?: string[];
  planIds?: string[];
  maxUses?: number;
  maxUsesPerUser?: number;
  validFrom?: string;
  validUntil?: string;
  isActive?: boolean;
}

export interface PromoCodeUsage {
  id: string;
  promoCodeId: string;
  userId: string;
  entityType: string;
  entityId?: string;
  discountAmount: number;
  originalAmount: number;
  finalAmount: number;
  appliedAt: string;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface PromoCodeAnalytics {
  totalUsage: number;
  totalRevenue: number;
  totalDiscount: number;
  conversionRate: number;
  topCodes: { code: string; usageCount: number; revenue: number }[];
  recentUsage: { date: string; count: number; discount: number }[];
  byDiscountType: { type: string; count: number; totalDiscount: number }[];
}

export type AddonFamily =
  | 'VISIBILITY_LIFECYCLE'
  | 'LISTING_ENRICHMENT'
  | 'AI_STUDIO'
  | 'TRUST_VERIFICATION'
  | 'PROFESSIONAL_CONTENT'
  | 'PERSONAL'
  | 'OTHER';
export type AddonAudience = 'OWNER' | 'BROKER' | 'COMPANY' | 'ALL';
export type AddonFulfillment = 'AUTO' | 'MANUAL_ORDER';
export type AddonPurchaseStatus =
  | 'PENDING_PAYMENT'
  | 'PENDING_FULFILLMENT'
  | 'ACTIVE'
  | 'FULFILLED'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'REFUNDED';

export interface Addon {
  id: string;
  slug: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  family: AddonFamily;
  fulfillment: AddonFulfillment;
  audiences: AddonAudience[];
  priceSar?: number | null;
  priceCoins?: number | null;
  isQuoteBased: boolean;
  durationDays?: number | null;
  metadata?: Record<string, unknown> | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAddonParams {
  slug: string;
  name: string;
  nameAr?: string;
  description?: string;
  descriptionAr?: string;
  family: AddonFamily;
  fulfillment?: AddonFulfillment;
  audiences: AddonAudience[];
  priceSar?: number | null;
  priceCoins?: number | null;
  isQuoteBased?: boolean;
  durationDays?: number | null;
  metadata?: Record<string, unknown> | null;
  isActive?: boolean;
  sortOrder?: number;
}

export interface AddonPurchase {
  id: string;
  userId: string;
  addonId: string;
  propertyId?: string | null;
  status: AddonPurchaseStatus;
  pricingMethod: 'SAR' | 'COINS';
  priceSar: number;
  priceCoins: number;
  discountSar: number;
  durationDays?: number | null;
  fulfillment: AddonFulfillment;
  orderNotes?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  bundlePurchaseId?: string | null;
  createdAt: string;
  addon?: Pick<Addon, 'id' | 'slug' | 'name' | 'nameAr' | 'family' | 'fulfillment' | 'isQuoteBased'>;
  user?: { id: string; email: string; firstName?: string; lastName?: string };
  property?: { id: string; title?: string; titleAr?: string } | null;
}

// ============================================================================
// ADD-ON BUNDLES
// ============================================================================

/** A single add-on slot inside a bundle, with its quantity + add-on detail. */
export interface AddonBundleItem {
  id?: string;
  bundleId?: string;
  addonId: string;
  quantity: number;
  addon?: Pick<Addon, 'id' | 'slug' | 'name' | 'nameAr' | 'priceSar' | 'isQuoteBased'>;
}

/** An add-on bundle (a fixed-price set of add-ons with one shared discount). */
export interface AddonBundle {
  id: string;
  slug: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  discountKind: DiscountKind;
  discountValue: number;
  audiences: AddonAudience[];
  isActive: boolean;
  sortOrder: number;
  items: AddonBundleItem[];
  /** Present in the admin list endpoint. */
  _count?: { purchases: number };
  createdAt: string;
  updatedAt: string;
}

export interface CreateBundleParams {
  slug: string;
  name: string;
  nameAr?: string;
  description?: string;
  descriptionAr?: string;
  discountKind: DiscountKind;
  discountValue: number;
  audiences: AddonAudience[];
  isActive?: boolean;
  sortOrder?: number;
  items: { addonId: string; quantity: number }[];
}

export interface UserSubscription {
  id: string;
  userId: string;
  planId: string;
  status: string;
  billingCycle: string;
  currentPeriodStart: string;
  currentPeriodEnd?: string;
  createdAt: string;
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  plan: {
    id: string;
    name: string;
    planType: string;
  };
}

// ============================================================================
// SERVICE METHODS
// ============================================================================

/**
 * Get auth token from the persisted session.
 */
function getToken(): string {
  return getAuthToken();
}

const adminService = {
  // ============================================================================
  // SUBSCRIPTION TIERS
  // ============================================================================

  /**
   * Get all tiers (including inactive).
   */
  async getTiers(): Promise<SubscriptionTier[]> {
    const response = await apiRequest<{ data: SubscriptionTier[] }>(
      '/subscriptions/admin/plans',
      {
        method: 'GET',
        token: getToken(),
      }
    );
    return response.data;
  },

  /**
   * Create a new tier.
   */
  async createTier(params: CreateTierParams): Promise<SubscriptionTier> {
    const response = await apiRequest<{ data: SubscriptionTier }>(
      '/subscriptions/plans',
      {
        method: 'POST',
        token: getToken(),
        body: params,
      }
    );
    return response.data;
  },

  /**
   * Update a tier.
   */
  async updateTier(id: string, params: Partial<CreateTierParams>): Promise<SubscriptionTier> {
    const response = await apiRequest<{ data: SubscriptionTier }>(
      `/subscriptions/plans/${id}`,
      {
        method: 'PUT',
        token: getToken(),
        body: params,
      }
    );
    return response.data;
  },

  /**
   * Delete (deactivate) a tier.
   */
  async deleteTier(id: string): Promise<void> {
    await apiRequest<void>(`/subscriptions/plans/${id}`, {
      method: 'DELETE',
      token: getToken(),
    });
  },

  // ============================================================================
  // PROMO CODES
  // ============================================================================

  /**
   * Get all promo codes.
   */
  async getPromoCodes(params?: {
    page?: number;
    pageSize?: number;
    isActive?: boolean;
  }): Promise<{ data: PromoCode[]; meta: { total: number; page: number; pageSize: number } }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));
    if (params?.isActive !== undefined) queryParams.set('isActive', String(params.isActive));

    const queryString = queryParams.toString();
    const path = queryString ? `/promo-codes?${queryString}` : '/promo-codes';

    const response = await apiRequest<{
      data: PromoCode[];
      meta: { total: number; page: number; pageSize: number };
    }>(path, {
      method: 'GET',
      token: getToken(),
    });
    return response;
  },

  /**
   * Get a single promo code with usage details.
   */
  async getPromoCode(id: string): Promise<PromoCode & { usages?: PromoCodeUsage[] }> {
    const response = await apiRequest<{ data: PromoCode & { usages?: PromoCodeUsage[] } }>(
      `/promo-codes/${id}`,
      {
        method: 'GET',
        token: getToken(),
      }
    );
    return response.data;
  },

  /**
   * Get promo code usage stats.
   */
  async getPromoCodeUsage(
    id: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<{
    data: PromoCodeUsage[];
    stats: {
      totalUsages: number;
      totalDiscountGiven: number;
      totalOriginalAmount: number;
      totalFinalAmount: number;
    };
    meta: { total: number; page: number; pageSize: number };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

    const queryString = queryParams.toString();
    const path = queryString
      ? `/promo-codes/${id}/usage?${queryString}`
      : `/promo-codes/${id}/usage`;

    return await apiRequest(path, {
      method: 'GET',
      token: getToken(),
    });
  },

  /**
   * Create a new promo code.
   */
  async createPromoCode(params: CreatePromoCodeParams): Promise<PromoCode> {
    const response = await apiRequest<{ data: PromoCode }>('/promo-codes', {
      method: 'POST',
      token: getToken(),
      body: params,
    });
    return response.data;
  },

  /**
   * Update a promo code.
   */
  async updatePromoCode(
    id: string,
    params: Partial<Omit<CreatePromoCodeParams, 'code'>>
  ): Promise<PromoCode> {
    const response = await apiRequest<{ data: PromoCode }>(`/promo-codes/${id}`, {
      method: 'PUT',
      token: getToken(),
      body: params,
    });
    return response.data;
  },

  /**
   * Delete (deactivate) a promo code.
   */
  async deletePromoCode(id: string): Promise<void> {
    await apiRequest<void>(`/promo-codes/${id}`, {
      method: 'DELETE',
      token: getToken(),
    });
  },

  /**
   * Get promo code analytics (CREG-11).
   * Aggregates usage data across all promo codes.
   */
  async getPromoCodeAnalytics(): Promise<PromoCodeAnalytics> {
    // This endpoint may not exist in backend yet - fallback to computing from usage data
    try {
      const response = await apiRequest<{ data: PromoCodeAnalytics }>(
        '/promo-codes/analytics',
        {
          method: 'GET',
          token: getToken(),
        }
      );
      return response.data;
    } catch {
      // Fallback: compute analytics client-side from available data
      const { data: codes } = await adminService.getPromoCodes({ pageSize: 100 });

      // Aggregate basic metrics
      const totalUsage = codes.reduce((sum, c) => sum + c.currentUses, 0);
      const totalDiscount = 0; // Would need usage data to compute
      const totalRevenue = 0; // Would need payment data to compute

      const topCodes = codes
        .filter((c) => c.currentUses > 0)
        .sort((a, b) => b.currentUses - a.currentUses)
        .slice(0, 5)
        .map((c) => ({
          code: c.code,
          usageCount: c.currentUses,
          revenue: 0,
        }));

      const byDiscountType = Object.entries(
        codes.reduce(
          (acc, c) => {
            const type = c.type;
            if (!acc[type]) acc[type] = { count: 0, totalDiscount: 0 };
            acc[type].count++;
            return acc;
          },
          {} as Record<string, { count: number; totalDiscount: number }>
        )
      ).map(([type, data]) => ({ type, ...data }));

      return {
        totalUsage,
        totalRevenue,
        totalDiscount,
        conversionRate: 0,
        topCodes,
        recentUsage: [],
        byDiscountType,
      };
    }
  },

  // ============================================================================
  // ADD-ONS CATALOG & ORDERS
  // ============================================================================

  /**
   * Get all add-ons (including inactive).
   */
  async getAddons(params?: { family?: string; isActive?: boolean }): Promise<Addon[]> {
    const q = new URLSearchParams();
    if (params?.family) q.set('family', params.family);
    if (params?.isActive !== undefined) q.set('isActive', String(params.isActive));
    const qs = q.toString();
    const response = await apiRequest<{ data: Addon[] }>(
      `/admin/addons${qs ? `?${qs}` : ''}`,
      { method: 'GET', token: getToken() }
    );
    return response.data;
  },

  /**
   * Create an add-on.
   */
  async createAddon(params: CreateAddonParams): Promise<Addon> {
    const response = await apiRequest<{ data: Addon }>('/admin/addons', {
      method: 'POST',
      token: getToken(),
      body: params,
    });
    return response.data;
  },

  /**
   * Update an add-on.
   */
  async updateAddon(id: string, params: Partial<CreateAddonParams>): Promise<Addon> {
    const response = await apiRequest<{ data: Addon }>(`/admin/addons/${id}`, {
      method: 'PUT',
      token: getToken(),
      body: params,
    });
    return response.data;
  },

  /**
   * Delete (or deactivate if it has purchases) an add-on.
   */
  async deleteAddon(id: string): Promise<void> {
    await apiRequest<void>(`/admin/addons/${id}`, { method: 'DELETE', token: getToken() });
  },

  /**
   * List add-on purchases / the manual-order fulfillment queue.
   */
  async getAddonPurchases(params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    family?: string;
    userId?: string;
    fulfillment?: string;
  }): Promise<{ data: AddonPurchase[]; meta: { total: number; page: number; pageSize: number } }> {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.pageSize) q.set('pageSize', String(params.pageSize));
    if (params?.status) q.set('status', params.status);
    if (params?.family) q.set('family', params.family);
    if (params?.userId) q.set('userId', params.userId);
    if (params?.fulfillment) q.set('fulfillment', params.fulfillment);
    const qs = q.toString();
    return await apiRequest(`/admin/addons/purchases${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      token: getToken(),
    });
  },

  /**
   * Update an add-on order: set a quote price, mark fulfilled, cancel or refund.
   */
  async updateAddonOrder(
    id: string,
    params: { priceSar?: number; status?: AddonPurchaseStatus; orderNotes?: string }
  ): Promise<AddonPurchase> {
    const response = await apiRequest<{ data: AddonPurchase }>(
      `/admin/addons/purchases/${id}`,
      { method: 'PATCH', token: getToken(), body: params }
    );
    return response.data;
  },

  // ============================================================================
  // ADD-ON BUNDLES (Phase 4)
  // ============================================================================

  /**
   * Get all add-on bundles (including inactive). Each bundle includes its
   * `items[]` (with the underlying add-on) and `_count.purchases`.
   */
  async getBundles(): Promise<AddonBundle[]> {
    const response = await apiRequest<{ data: AddonBundle[] }>('/admin/addon-bundles', {
      method: 'GET',
      token: getToken(),
    });
    return response.data;
  },

  /**
   * Create an add-on bundle.
   * 409 SLUG_EXISTS; 400 QUOTE_ADDON_IN_BUNDLE (quote-based add-ons can't be bundled).
   */
  async createBundle(params: CreateBundleParams): Promise<AddonBundle> {
    const response = await apiRequest<{ data: AddonBundle }>('/admin/addon-bundles', {
      method: 'POST',
      token: getToken(),
      body: params,
    });
    return response.data;
  },

  /**
   * Update an add-on bundle. `items` (when provided) replaces the existing set.
   */
  async updateBundle(id: string, params: Partial<CreateBundleParams>): Promise<AddonBundle> {
    const response = await apiRequest<{ data: AddonBundle }>(`/admin/addon-bundles/${id}`, {
      method: 'PUT',
      token: getToken(),
      body: params,
    });
    return response.data;
  },

  /**
   * Delete (or deactivate if it has purchases) an add-on bundle.
   */
  async deleteBundle(id: string): Promise<void> {
    await apiRequest<void>(`/admin/addon-bundles/${id}`, {
      method: 'DELETE',
      token: getToken(),
    });
  },

  // ============================================================================
  // USER SUBSCRIPTIONS (CREG-10)
  // ============================================================================

  /**
   * Get all user subscriptions.
   */
  async getUserSubscriptions(params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    planId?: string;
    search?: string;
  }): Promise<{ data: UserSubscription[]; meta: { total: number; page: number } }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));
    if (params?.status) queryParams.set('status', params.status);
    if (params?.planId) queryParams.set('planId', params.planId);
    if (params?.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    const path = queryString
      ? `/subscriptions/admin/all?${queryString}`
      : '/subscriptions/admin/all';

    return await apiRequest(path, {
      method: 'GET',
      token: getToken(),
    });
  },

  /**
   * Assign a tier to a specific user (CREG-10).
   * Used for custom deals, enterprise agreements, or admin overrides.
   */
  async assignTierToUser(
    userId: string,
    tierId: string,
    billingCycle: 'monthly' | 'yearly'
  ): Promise<UserSubscription> {
    const response = await apiRequest<{ data: UserSubscription }>(
      '/admin/assign-tier',
      {
        method: 'POST',
        token: getToken(),
        body: {
          userId,
          planId: tierId,
          billingCycle,
        },
      }
    );
    return response.data;
  },

  /**
   * Cancel a user's subscription.
   */
  async cancelUserSubscription(subscriptionId: string, reason?: string): Promise<void> {
    await apiRequest<void>(`/subscriptions/admin/${subscriptionId}/cancel`, {
      method: 'POST',
      token: getToken(),
      body: { reason },
    });
  },

  // ============================================================================
  // FAL LICENSE VERIFICATION (REG9-03)
  // ============================================================================

  /**
   * Update an agent's FAL verification status.
   * Admin can approve, reject (with reason), or reset to pending.
   */
  async updateAgentFalStatus(
    agentId: string,
    status: 'PENDING' | 'APPROVED' | 'REJECTED',
    rejectionReason?: string
  ): Promise<{ message: string; agent: unknown }> {
    const response = await apiRequest<{ message: string; agent: unknown }>(
      `/admin/agents/${agentId}/fal-status`,
      {
        method: 'PUT',
        token: getToken(),
        body: {
          status,
          rejectionReason,
        },
      }
    );
    return response;
  },

  /**
   * Get all agents with FAL verification data for admin review.
   */
  async getAgentsForFalReview(params?: {
    page?: number;
    pageSize?: number;
    falStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  }): Promise<{
    data: Array<{
      id: string;
      userId: string;
      licenseNumber?: string;
      falVerificationStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
      falRejectionReason?: string;
      user: {
        id: string;
        email: string;
        firstName?: string;
        lastName?: string;
        accountType?: string;
      };
    }>;
    meta: { total: number; page: number; pageSize: number };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));
    if (params?.falStatus) queryParams.set('falStatus', params.falStatus);

    const queryString = queryParams.toString();
    const path = queryString ? `/admin/agents?${queryString}` : '/admin/agents';

    return await apiRequest(path, {
      method: 'GET',
      token: getToken(),
    });
  },

  // ============================================================================
  // COINS (Phase 2)
  // ============================================================================

  /**
   * Update the coin↔SAR rate (SUPER_ADMIN only — the API enforces this).
   * Re-auth-gated: the apiClient injects X-Reauth-Token transparently.
   */
  async setCoinRate(params: { sarPerCoin: number }): Promise<unknown> {
    const response = await apiRequest<{ data: unknown }>('/admin/settings/coin-rate', {
      method: 'PUT',
      token: getToken(),
      body: params,
    });
    return response.data;
  },

  /**
   * Grant (credit) or deduct (debit) coins for a specific user.
   * Re-auth-gated: the apiClient handles the X-Reauth-Token flow transparently.
   */
  async grantUserCoins(
    userId: string,
    params: { amount: number; direction: 'credit' | 'debit'; reason: string }
  ): Promise<{ balance: number; transactionId: string }> {
    const response = await apiRequest<{ data: { balance: number; transactionId: string } }>(
      `/admin/users/${userId}/coins`,
      {
        method: 'POST',
        token: getToken(),
        body: params,
      }
    );
    return response.data;
  },

  /**
   * Get a user's coin balance and recent ledger.
   */
  async getUserCoins(
    userId: string
  ): Promise<{ balance: number; transactions: CoinTransaction[] }> {
    const response = await apiRequest<{
      data: { balance: number; transactions: CoinTransaction[] };
    }>(`/admin/users/${userId}/coins`, {
      method: 'GET',
      token: getToken(),
    });
    return response.data;
  },

  // ==========================================================================
  // REAL-ESTATE DEVELOPERS
  // ==========================================================================

  async getDevelopers(params?: { skip?: number; limit?: number; search?: string; status_filter?: string; verified_filter?: string }): Promise<{ developers: AdminDeveloperRow[]; total: number }> {
    const qs = new URLSearchParams();
    if (params?.skip != null) qs.set('skip', String(params.skip));
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.search) qs.set('search', params.search);
    if (params?.status_filter) qs.set('status_filter', params.status_filter);
    if (params?.verified_filter) qs.set('verified_filter', params.verified_filter);
    return apiRequest(`/admin/developers${qs.toString() ? `?${qs}` : ''}`, { method: 'GET', token: getToken() });
  },

  async getDeveloperDetail(id: string): Promise<AdminDeveloperDetail> {
    return apiRequest(`/admin/developers/${id}`, { method: 'GET', token: getToken() });
  },

  async verifyDeveloper(id: string, verified: boolean): Promise<unknown> {
    return apiRequest(`/admin/developers/${id}/${verified ? 'verify' : 'unverify'}`, { method: 'POST', token: getToken() });
  },

  async updateDeveloperStatus(id: string, isActive: boolean): Promise<unknown> {
    return apiRequest(`/admin/developers/${id}/status`, { method: 'PUT', token: getToken(), body: { is_active: isActive } });
  },

  async updateDeveloper(id: string, data: Record<string, unknown>): Promise<unknown> {
    return apiRequest(`/admin/developers/${id}`, { method: 'PUT', token: getToken(), body: data });
  },

  async getInvestorLeads(params?: { skip?: number; limit?: number; role_filter?: string; status_filter?: string }): Promise<{ leads: InvestorLeadRow[]; total: number }> {
    const qs = new URLSearchParams();
    if (params?.skip != null) qs.set('skip', String(params.skip));
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.role_filter) qs.set('role_filter', params.role_filter);
    if (params?.status_filter) qs.set('status_filter', params.status_filter);
    return apiRequest(`/admin/investor-leads${qs.toString() ? `?${qs}` : ''}`, { method: 'GET', token: getToken() });
  },

  async updateInvestorLead(id: string, data: { status?: string; adminNotes?: string }): Promise<unknown> {
    return apiRequest(`/admin/investor-leads/${id}`, { method: 'PATCH', token: getToken(), body: data });
  },
};

export interface AdminDeveloperRow {
  id: string;
  name: string;
  name_ar?: string | null;
  slug: string;
  logo_url?: string | null;
  email?: string | null;
  phone?: string | null;
  license_number?: string | null;
  headquarters_city?: string | null;
  is_active: boolean;
  is_verified: boolean;
  verified_at?: string | null;
  project_count: number;
  listing_count: number;
  team_count: number;
  created_at: string;
}

export interface AdminDeveloperDetail extends AdminDeveloperRow {
  description?: string | null;
  cover_url?: string | null;
  website?: string | null;
  address?: string | null;
  founded_year?: number | null;
  avg_rating: number;
  total_reviews: number;
  active_listings: number;
  owner?: { id: string; email: string; firstName?: string | null; lastName?: string | null } | null;
  team: Array<{ id: string; role: string; status: string; user: { id: string; email: string; firstName?: string | null; lastName?: string | null } }>;
  projects: Array<{ id: string; name: string; slug: string; status: string; investmentStatus: string; isActive: boolean; isFeatured: boolean }>;
  licenses: Array<{ id: string; licenseType: string; licenseNumber: string; status: string; expiryDate?: string | null; isActive: boolean }>;
}

export interface InvestorLeadRow {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  role: 'DEVELOPER' | 'INVESTOR';
  projectOrBudget?: string | null;
  message?: string | null;
  status: 'NEW' | 'CONTACTED' | 'CLOSED';
  createdAt: string;
  company?: { id: string; name: string; slug: string } | null;
}

export default adminService;
